"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, FileCheck2, Hammer, ShieldCheck, ArrowRight, Calendar, CircleCheck as CheckCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface ProcessStep {
  number: string;
  title: string;
  duration: string;
  summary: string;
  deliverables: string;
}
const steps: ProcessStep[] = [{
  number: "01",
  title: "Consultation & site assessment",
  duration: "Week 1–2",
  summary: "On-site architectural evaluation, feasibility study, zoning review, and preliminary budget framework.",
  deliverables: "Site survey · Feasibility report · Budget estimate"
}, {
  number: "02",
  title: "Design & pre-construction",
  duration: "Week 3–6",
  summary: "Detailed 3D architectural modeling, material specifications, City of Decatur permits, and a fixed-price contract.",
  deliverables: "3D architectural plans · Permit filings · Fixed scope"
}, {
  number: "03",
  title: "Craftsmanship & build",
  duration: "Week 7–16",
  summary: "Dedicated site supervisor, weekly milestone updates, HEPA dust containment, and master carpenter execution.",
  deliverables: "Daily site supervision · Portal updates · Dust control"
}, {
  number: "04",
  title: "Final walkthrough",
  duration: "Final Week",
  summary: "Zero-punchlist walk-through and care package handover.",
  deliverables: "Punchlist sign-off · Post-care guide"
}];
const stepIcons = [Compass, FileCheck2, Hammer, ShieldCheck];
export default function Process() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="our-process" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 sm:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-sans text-xs tracking-wider uppercase bg-secondary text-secondary-foreground border border-border">
            Proven framework
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-5 leading-tight">
            Our 4-step renovation process
          </h2>
          <p className="text-muted-foreground font-sans text-base sm:text-lg leading-relaxed">
            Every Decatur project follows a structured, milestone-driven pathway.
            You always know what is happening next, when it will be finished, and
            what it costs.
          </p>
        </div>

        {/* Process Steps Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-6">
          {steps.map((step, i) => {
          const IconComponent = stepIcons[i % stepIcons.length];
          const deliverableList = step.deliverables.split(" · ");
          return <motion.div key={step.number} data-index={i} variants={itemVariants} className="h-full">
                <Card className="h-full bg-card text-card-foreground border border-border shadow-xs hover:shadow-md hover:border-border/80 transition-[box-shadow,border-color] duration-300 flex flex-col justify-between relative overflow-hidden rounded-xl">
                  {/* Architectural Accent Hairline */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-border" />

                  <CardHeader className="p-6 pb-4">
                    {/* Header Row: Step Number & Icon */}
                    <div className="flex items-center justify-between mb-4">
                      <span className="inline-flex items-center justify-center font-mono text-xs font-semibold px-2.5 py-1 rounded-full bg-secondary text-foreground border border-border">
                        Step {step.number}
                      </span>
                      <div className="size-10 rounded-lg bg-secondary text-foreground flex items-center justify-center border border-border/80">
                        <IconComponent className="size-5" />
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium mb-2">
                      <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                      <span>{step.duration}</span>
                    </div>

                    <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-foreground leading-snug">
                      {step.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-6 pt-0 flex-1 flex flex-col justify-between gap-6">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {step.summary}
                    </p>

                    <div className="pt-4 border-t border-border/60">
                      <span className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
                        Key deliverables
                      </span>
                      <ul className="space-y-1.5">
                        {deliverableList.map((item, itemIdx) => <li key={itemIdx} data-index={itemIdx} className="flex items-start gap-2 text-xs text-foreground/90 leading-tight">
                            <CheckCircle className="size-3.5 text-muted-foreground shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom CTA Strip */}
        <div className="mt-14 sm:mt-16 p-6 sm:p-8 rounded-2xl bg-secondary/50 border border-border flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div className="max-w-2xl">
            <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground mb-1.5">
              Ready to start planning your home renovation?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              Schedule an on-site consultation to review your property, goals, and preliminary budget.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-6 py-2.5 text-sm font-medium shadow-xs">
              <Link to="/ContactPage">
                <span>Book a consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto border-border bg-card text-foreground hover:bg-secondary rounded-md px-5 py-2.5 text-sm font-medium">
              <Link to="/ProjectsPage">View recent projects</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}